import type { VerificationChallenge } from '@tenfour/contracts'
import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { passkeyLogin } from '@/lib/passkey'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
  onboardingStatus: string
  // Admin-created accounts start with both false. One of them flipping
  // true is the gate that unlocks the setup wizard — until then, AuthGuard
  // routes the account to /secure-account.
  passwordResetCompleted?: boolean
  ssoLinked?: boolean
  // Multi-user: which person on the account is signed in, and their account-level
  // role. Present on tokens issued after the multi-user re-key; absent on legacy.
  userId?: string
  accountRole?: 'OWNER' | 'MEMBER'
  // Per-user capability flags for UI gating. Absent on legacy sessions → treat
  // as OWNER (allowed). Use the `can*` helper below rather than reading directly.
  canGenerateInvoices?: boolean
  canMarkInvoicePaid?: boolean
  canCancelJobs?: boolean
  canAdjustSettings?: boolean
  canViewFormResponses?: boolean
  // True while staff are signed in to this account through the admin console's
  // "Log in as owner". Rides the session (and survives a token refresh) so the
  // portal can say so for as long as the session lasts.
  impersonated?: boolean
  canViewReports?: boolean
}

/**
 * Mirror of the server's `can()` for the per-user capabilities: an OWNER
 * (or a legacy session with no accountRole) can do everything; a MEMBER is
 * governed by the boolean flag. UI-only convenience — the server still enforces.
 */
export function userCan(
  user: AuthUser | null,
  cap:
    | 'canGenerateInvoices'
    | 'canMarkInvoicePaid'
    | 'canCancelJobs'
    | 'canAdjustSettings'
    | 'canViewFormResponses'
    | 'canViewReports',
): boolean {
  if (!user) return false
  if (user.accountRole !== 'MEMBER') return true
  // #517 transition, mirroring normalizeCurrentUser on the server: a cached
  // member session from before form responses got their own permission carries
  // no flag, and under the old rule their access WAS canAdjustSettings. Only an
  // ABSENT flag falls back; an explicit false (permission withheld) stays false.
  if (cap === 'canViewFormResponses' && user.canViewFormResponses === undefined) {
    return user.canAdjustSettings === true
  }
  // #572 transition, same shape: revenue used to ride the invoice permission
  // (the migration backfilled can_view_reports from can_generate_invoices), so
  // an absent flag on a cached member session falls back to it until re-key.
  if (cap === 'canViewReports' && user.canViewReports === undefined) {
    return user.canGenerateInvoices === true
  }
  return user[cap] === true
}

/**
 * Mirror of the server's OwnerGuard for UI gating: platform ADMIN and the
 * account OWNER (or a legacy session with no accountRole) pass; an explicit
 * MEMBER does not. Use for OWNER-only actions (delete/import/export customers).
 * UI-only convenience — the server still enforces via OwnerGuard.
 */
export function isOwner(user: AuthUser | null): boolean {
  if (!user) return false
  if (user.role === 'ADMIN') return true
  return user.accountRole !== 'MEMBER'
}

interface AuthContextType {
  user: AuthUser | null
  accountId: string | null
  loading: boolean
  onboardingComplete: boolean
  accountSecured: boolean
  signIn: (email: string, password: string, code?: string, rememberDevice?: boolean) => Promise<{ twoFactorRequired: boolean; challenge?: VerificationChallenge }>
  signInWithPasskey: (email?: string) => Promise<void>
  /** Persist a server-minted session without going through login (outreach signup, etc.). */
  establishSession: (data: { token: string; user: AuthUser }) => void
  signOut: () => Promise<void>
  updateOnboardingStatus: (status: string) => void
  markAccountSecured: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

// The contractor session this browser has stored: the cached user when both
// tt_token and tt_user are present (the mount check's rule), else null.
function readStoredUser(): AuthUser | null {
  const token = localStorage.getItem('tt_token')
  const savedUser = localStorage.getItem('tt_user')
  if (!token || !savedUser) return null
  try {
    return JSON.parse(savedUser) as AuthUser
  } catch {
    return null
  }
}

// Whose session it is: the account and, on multi-user accounts, the member.
function sessionIdentity(user: AuthUser | null): string | null {
  return user ? `${user.id}:${user.userId ?? ''}` : null
}

/**
 * Keeps this tab's user in step with the session other tabs store. The
 * `storage` event fires only in the OTHER tabs of the origin: a sign-out or a
 * terminal refresh failure there signs this tab out, and a sign-in, an admin
 * impersonation or a cached-user update there replaces this tab's user. A
 * token rotation (tt_token replaced, tt_user untouched) changes nothing here.
 */
function useStoredSessionSync(onChange: (user: AuthUser | null) => void) {
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== localStorage) return
      // key === null: another tab called localStorage.clear().
      if (e.key !== null && e.key !== 'tt_token' && e.key !== 'tt_user') return
      const next = readStoredUser()
      if (next && e.key === 'tt_token' && e.oldValue !== null) return
      onChange(next)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [onChange])
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [accountId, setAccountId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('tt_token')
    const savedUser = localStorage.getItem('tt_user')
    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser) as AuthUser
        setUser(parsed)
        setAccountId(parsed.id)
      } catch {
        localStorage.removeItem('tt_token')
        localStorage.removeItem('tt_refresh_token')
        localStorage.removeItem('tt_user')
      }
    }
    setLoading(false)
  }, [])

  // The api() helper clears localStorage + fires this event when a refresh
  // rotation fails (terminal 401). AuthGuard reads `user` and redirects to
  // /login once we reset it here.
  useEffect(() => {
    const onCleared = () => {
      setUser(null)
      setAccountId(null)
    }
    window.addEventListener('tt:auth:cleared', onCleared)
    return () => window.removeEventListener('tt:auth:cleared', onCleared)
  }, [])

  // Another tab signed in, signed out, or updated the cached user: mirror it,
  // and drop this tab's cached queries when the person changes — as signOut
  // and persistSession do for this tab's own changes.
  const syncStoredUser = useCallback((next: AuthUser | null) => {
    if (sessionIdentity(next) !== sessionIdentity(user)) queryClient.clear()
    setUser(next)
    setAccountId(next?.id ?? null)
  }, [user, queryClient])
  useStoredSessionSync(syncStoredUser)

  const signIn = async (email: string, password: string, code?: string, rememberDevice?: boolean) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // include credentials so a previously-set trusted-device cookie is sent
      // (server skips the 2FA prompt) and the rotated auth cookies are stored.
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        ...(code ? { code } : {}),
        ...(rememberDevice ? { rememberDevice: true } : {}),
      }),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message ?? 'Login failed')
    }

    const data = await res.json()
    // 2FA is on and we didn't send a (valid) code yet — the page prompts for one.
    if (data.twoFactorRequired) {
      return { twoFactorRequired: true, challenge: data as VerificationChallenge }
    }
    persistSession(data)
    return { twoFactorRequired: false }
  }

  // Stores tokens + user from a successful login response (password or passkey)
  // and updates context state. Throws for admin accounts (wrong portal).
  const persistSession = (data: any): AuthUser => {
    const authUser: AuthUser = data.user
    if (authUser.role === 'ADMIN') {
      throw new Error('This login is for accounts only. For admin access, navigate to /admin in your browser URL bar.')
    }
    localStorage.setItem('tt_token', data.token)
    // The refresh + trusted-device tokens are now httpOnly cookies set by the
    // server (no longer in JS-readable localStorage). Clean up any stale values
    // left by a pre-cookie session.
    localStorage.removeItem('tt_refresh_token')
    localStorage.removeItem('tt_trusted_device')
    localStorage.setItem('tt_user', JSON.stringify(authUser))
    // Drop any cached React Query data from a previous session — members on the
    // same account share an accountId, so user-scoped responses (e.g. reminders)
    // would otherwise be served from the prior user's cache until a refetch.
    queryClient.clear()
    setUser(authUser)
    setAccountId(authUser.id)
    return authUser
  }

  // Passkey (WebAuthn) sign-in: runs the browser ceremony, then persists the
  // session the server mints. A passkey already proves possession, so there's
  // no separate 2FA step.
  const signInWithPasskey = async (email?: string) => {
    const data = await passkeyLogin(email)
    persistSession(data)
  }

  // Same persistence as password/passkey login — used by the public outreach
  // signup so "Open my dashboard" lands signed in without a remount (writing
  // localStorage alone left AuthContext.user null and AuthGuard bounced to /login).
  const establishSession = (data: { token: string; user: AuthUser }) => {
    persistSession(data)
  }

  const signOut = async () => {
    // Clear the httpOnly auth cookies server-side (JS can't touch them).
    try {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' })
    } catch { /* best-effort — still clear local state below */ }
    localStorage.removeItem('tt_token')
    localStorage.removeItem('tt_refresh_token')
    localStorage.removeItem('tt_trusted_device')
    localStorage.removeItem('tt_user')
    // Wipe cached data so the next user doesn't briefly see this session's.
    queryClient.clear()
    setUser(null)
    setAccountId(null)
  }

  const updateOnboardingStatus = (status: string) => {
    if (user) {
      const updated = { ...user, onboardingStatus: status }
      setUser(updated)
      localStorage.setItem('tt_user', JSON.stringify(updated))
    }
  }

  const markAccountSecured = () => {
    if (user) {
      const updated = { ...user, passwordResetCompleted: true }
      setUser(updated)
      localStorage.setItem('tt_user', JSON.stringify(updated))
    }
  }

  // TWILIO_PENDING means the user finished the setup wizard but the worker is
  // still provisioning their phone number — let them into the dashboard.
  const onboardingComplete = user?.onboardingStatus === 'COMPLETE' || user?.onboardingStatus === 'TWILIO_PENDING'
  // True when the account has secured their account (reset the admin-given
  // password OR linked SSO). Admins skip this gate. Pre-existing sessions that
  // pre-date this field (no flag in the cached user) are treated as secured.
  const accountSecured =
    !user ||
    user.role === 'ADMIN' ||
    user.passwordResetCompleted === undefined ||
    user.passwordResetCompleted === true ||
    user.ssoLinked === true

  return (
    <AuthContext.Provider value={{ user, accountId, loading, onboardingComplete, accountSecured, signIn, signInWithPasskey, establishSession, signOut, updateOnboardingStatus, markAccountSecured }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
